import classNames from 'classnames';
import { AnimatePresence, m as motion } from 'framer-motion';
import { Fragment, forwardRef, useState } from 'react';
import { useIsMounted } from 'usehooks-ts';
import { Document } from '@/entities/document';
import { Schema } from '@/entities/schema';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import { tp } from '@/shared/lib/typograf';
import SchemaPopup, { SCHEMA_POPUP_NAME } from '../../popups/SchemaPopup/SchemaPopup';
import Button from '../../shared/Button';
import Image from '../../shared/Image';
import Responsive from '../../shared/Responsive';
import SectionDocs from '../../shared/SectionDocs';
import Typografed from '../../shared/Typografed';
import SchemaSwitcher from './SchemaSwitcher';

export type RawProps = {
    title: string;
    documents: Document[];
    schemas: Schema[];
};

export type Props = React.HTMLAttributes<HTMLElement> & RawProps;

const SchemaSection = forwardRef<HTMLDivElement, Props>(({ title, documents, schemas, ...props }, ref) => {
    const [activeSchemaIndex, setActiveSchemaIndex] = useState(0);
    const { openPopup } = useSetOpenedPopupsState();
    const device = useMediaQueryDeviceStateValue();
    const isMobile = device.includes('mobile');
    const isMounted = useIsMounted();
    const shouldRenderPopup = isMobile && isMounted();

    return (
        <section {...props} ref={ref} className={classNames('schema-section wrapper', props.className)}>
            <div className="schema-section-header">
                <h2 className="h3 schema-section-title">
                    <Typografed>{title}</Typografed>
                </h2>
                {documents.length > 0 && <SectionDocs documents={documents} className="schema-section-doc" />}
            </div>

            <div className="schema-section-content">
                <div className="schema-section-schema">
                    <Responsive className="schema-section-img-wrapper">
                        <div>
                            {schemas.length > 1 && (
                                <SchemaSwitcher
                                    schemas={schemas}
                                    activeSchemaIndex={activeSchemaIndex}
                                    setActiveSchemaIndex={setActiveSchemaIndex}
                                    className="schema-section-schema-list"
                                />
                            )}
                            {schemas.map((schema, i) => (
                                <Fragment key={i}>
                                    <Image
                                        src={schema.img.src}
                                        alt={schema.img.alt || ''}
                                        width={schema.img.width}
                                        height={schema.img.height}
                                        title={schema.img.title}
                                        sizes="(max-width: 1199px) 100vw, 62vw"
                                        hidden={i !== activeSchemaIndex}
                                        className="responsive__item fade-in"
                                    />
                                    <Button
                                        variant="primary-filled"
                                        className="schema-section-schema__image-btn fade-in"
                                        onClick={() => {
                                            if (shouldRenderPopup) {
                                                openPopup(SCHEMA_POPUP_NAME);
                                            }
                                        }}
                                        hidden={i !== activeSchemaIndex}
                                    >
                                        Вся схема
                                    </Button>
                                </Fragment>
                            ))}
                        </div>
                    </Responsive>
                </div>

                <AnimatePresence mode="wait">
                    {schemas[activeSchemaIndex].text && (
                        <motion.div
                            key={activeSchemaIndex}
                            className="schema-section-info-wrapper"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <div
                                className="schema-section-info wysiwyg"
                                dangerouslySetInnerHTML={{ __html: tp(schemas[activeSchemaIndex].text) }}
                            ></div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {shouldRenderPopup && (
                <SchemaPopup
                    image={schemas[activeSchemaIndex].img}
                    overflow={true}
                    topSlot={
                        schemas.length > 1 && (
                            <SchemaSwitcher
                                schemas={schemas}
                                activeSchemaIndex={activeSchemaIndex}
                                setActiveSchemaIndex={setActiveSchemaIndex}
                                className="schema-popup-schema-list"
                            />
                        )
                    }
                />
            )}
        </section>
    );
});

SchemaSection.displayName = 'SchemaSection';

export default SchemaSection;
